import { rem } from 'polished'

import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import * as React from 'react'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import { useAppDispatch } from 'hooks/reduxHooks'
import Pane from 'components/Common/Pane'
import CurrencyContext from 'contexts/currencyContext'
import ExperienceTicketSelection from '../ExperienceTickets/ExperienceTicketSelection'
import ExperienceTicketInputs from '../ExperienceTickets/ExperienceTicketInputs'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import type { ExperienceDetailsForm } from '../ExperienceDetailsModal/ExperienceDetailsBookingModal'
import { formToObject } from 'lib/forms/formToObject'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { arrayToObject, sum, max } from 'lib/array/arrayUtils'
import { generateExperienceCheckoutItem } from 'lib/checkout/experiences/cart'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import ExperiencePageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import ExperiencePageDispatchContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageDispatchContext'
import { ExperienceOfferPageActions } from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'
import { arrayToStringListFormat } from 'lib/string/stringUtils'
import { addExperienceToCheckout } from 'checkout/lib/analytics/checkoutUAMappers'
import { addGTMEvent, fireEcommerceGTMEvent } from 'api/googleTagManager'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { addToCartGTM } from 'analytics/eventDefinitions'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { generateExperienceGiftCardItem } from 'lib/checkout/gift/cart'
import type { FetchDateParams } from 'lib/experiences/experienceUtils'
import { getTotalSeats } from 'lib/experiences/experienceUtils'
import ExperienceTicketCustomerLimit from '../ExperienceTickets/ExperienceTicketCustomerLimit'
import useExperienceAvailableDates from 'hooks/ExperiencePage/useExperienceAvailableDates'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import { useExperienceTicketsOptions } from 'hooks/ExperiencePage/useExperienceTicketsOptions'
import usePrevious from 'hooks/usePrevious'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { addToUserCart } from 'actions/UserCartActions'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { isMultiCartExperienceSupported } from 'lib/checkout/userCart'
import { addToCart, addToCartMultiItem } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { scrollToId } from 'lib/window/scrollUtils'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { queryKeyDate } from 'constants/url'
import ExperienceOfferPageBuyBar from './ExperienceOfferPageBuyBar'

const TicketContainer = styled(Pane)`
  padding: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  position: relative;
`

const TicketContainerWithoutPadding = styled(TicketContainer)`
  padding: 0;
`

const TicketInputContainer = styled.div`
  padding: ${rem(24)};
`

type SubmitAction = 'buyNow' | 'keepBrowsing' | 'addToUserCart'

function ticketModeMapsEqual(
  left: Record<string, string> | undefined,
  right: Record<string, string> | undefined,
) {
  const leftEntries = Object.entries(left ?? {}).filter(([, value]) => !!value)
  const rightMap = Object.fromEntries(Object.entries(right ?? {}).filter(([, value]) => !!value))
  return leftEntries.length === Object.keys(rightMap).length && leftEntries.every(([key, value]) => rightMap[key] === value)
}

interface Props {
  experienceView: App.ExperienceBookingView
  isBuyNowBookLater?: boolean
  isGift?: boolean
  luxPlusEnabled: boolean
  secondaryAction?: React.ReactElement
  showAddToCartAction?: boolean
  checkoutSource?: App.CheckoutSource
  preserveSelectedDateWhenDateOptionsEmpty?: boolean
}

const ExperienceOfferPageTicketSelection = forwardRef<HTMLFormElement, Props>((props, formRef) => {
  const {
    experienceView,
    isBuyNowBookLater = false,
    isGift = false,
    luxPlusEnabled,
    secondaryAction,
    showAddToCartAction = true,
    checkoutSource,
    preserveSelectedDateWhenDateOptionsEmpty = false,
  } = props

  const { search } = useLocation()
  const initialDateFromUrl = useRef(parseSearchString(search)[queryKeyDate])
  const internalFormRef = useRef<HTMLFormElement>(null)
  const [hasNoTicketsSelected, setHasNoTicketsSelected] = useState(false)
  useImperativeHandle(formRef, () => internalFormRef.current!)
  const currency = useContext(CurrencyContext)
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const experience = experienceView.originalExperience

  const collinsonValidityNote = experience.provider === 'cln' ? (
    <BodyTextBlock variant="medium" startIcon={<LineCheckCircleIcon colour="success" size="S" />} style={{ marginTop: rem(8) }}>
      {intl.formatMessage(
        {
          id: 'k6Yi__gAT5KqDRR-nS_O',
          defaultMessage: 'Valid for <b>12 months</b> from the date of purchase',
          description: 'Validity period message for Collinson lounge passes',
        },
        { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
      )}
    </BodyTextBlock>
  ) : undefined

  const userMultiItemCartSupported = showAddToCartAction && isMultiCartExperienceSupported(experience, isGift)

  const trackExperienceOfferEvent = useTrackExperienceOfferEvent()

  const handleOptionPurchaseEvent = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackExperienceOfferEvent('BNBL_BUY_NOW_CLICKED')
    }
    if (isGift) {
      return trackExperienceOfferEvent('GIFT_BUY_NOW_CLICKED')
    }
    return trackExperienceOfferEvent('BUY_NOW_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackExperienceOfferEvent is stable
  }, [isBuyNowBookLater, isGift])

  const onTicketCountIncrease = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackExperienceOfferEvent('BNBL_INCREASE_OPTION_SELECTED')
    }
    if (isGift) {
      return trackExperienceOfferEvent('GIFT_INCREASE_OPTION_SELECTED')
    }
    return trackExperienceOfferEvent('INCREASE_OPTION_SELECTED')
  }, [isBuyNowBookLater, isGift]) // oxlint-disable-line react-hooks/exhaustive-deps -- trackExperienceOfferEvent is stable

  const onTicketCountDecrease = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackExperienceOfferEvent('BNBL_DECREASE_OPTION_SELECTED')
    }
    if (isGift) {
      return trackExperienceOfferEvent('GIFT_DECREASE_OPTION_SELECTED')
    }
    return trackExperienceOfferEvent('DECREASE_OPTION_SELECTED')
  }, [isBuyNowBookLater, isGift]) // oxlint-disable-line react-hooks/exhaustive-deps -- trackExperienceOfferEvent is stable

  const onTicketCountChange = useCallback((increment: boolean) => {
    if (increment) {
      return onTicketCountIncrease()
    }
    return onTicketCountDecrease()
  }, [onTicketCountDecrease, onTicketCountIncrease])

  const pageState = useContext(ExperiencePageStateContext)
  const { date, time, pickupPoint: selectedPickupPoint, ticketCounts, redemptionLocation: selectedLocation, language: selectedLanguage, ticketModesMap: selectedTicketModesMap } = pageState
  const experienceDispatch = useContext(ExperiencePageDispatchContext)

  const setTicketCounts = useCallback((ticketCounts: Record<string, number>) => experienceDispatch({ type: ExperienceOfferPageActions.setTicketCounts, ticketCounts }), [experienceDispatch])

  const setDate = useCallback((date?: string) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setDate, date })
  }, [experienceDispatch])

  const setSelectedPickupPoint = useCallback((pickupPoint?: App.ExperiencePickupPoint) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setPickupPoint, pickupPoint })
  }, [experienceDispatch])

  const setTimeSlot = useCallback((time: string) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setTime, time })
  }, [experienceDispatch])

  const setSelectedLocation = useCallback((redemptionLocation?: App.ExperienceRedemptionLocation) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setRedemptionLocation, redemptionLocation })
  }, [experienceDispatch])

  const setLanguage = useCallback((language?: App.ExperienceLanguage) => {
    if (!language) return
    experienceDispatch({ type: ExperienceOfferPageActions.setLanguage, language })
  }, [experienceDispatch])

  const setSelectedTicketMode = useCallback((ticketModesMap?: Record<string, string>) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setTicketMode, ticketModesMap })
  }, [experienceDispatch])

  const fetchDateParams = useMemo(() => ({
    pickupPointId: selectedPickupPoint?.id,
    redemptionLocationId: selectedLocation?.id,
    ticketModesMap: selectedTicketModesMap,
    isBuyNowBookLater,
    isGift,
  }), [selectedPickupPoint, selectedLocation, selectedTicketModesMap, isBuyNowBookLater, isGift])
  const [availableDates, fetchingDates, datesError] = useExperienceAvailableDates(experienceView.originalExperience, fetchDateParams)

  const fetchTimeParams = useMemo(() => ({
    date: isBuyNowBookLater || isGift ? undefined : date,
    redemptionLocationId: selectedLocation?.id,
    pickupPointId: selectedPickupPoint?.id,
    isBuyNowBookLater,
    isGift,
    ticketModesMap: selectedTicketModesMap,
  }), [selectedPickupPoint, selectedLocation, selectedTicketModesMap, isBuyNowBookLater, isGift, date])
  const [availableTimes, fetchingTimeSlots, timeSlotsError] = useExperienceAvailableTimes(experienceView.originalExperience, fetchTimeParams)

  const loading = !!(fetchingDates || fetchingTimeSlots)

  const previousAvailableDates = usePrevious(availableDates)

  const loadDates = useCallback(() => {
    if (!experience) return

    const fetchParams: FetchDateParams = {}

    const locations = experience.redemptionLocations
    if (locations?.length === 1) {
      const defaultLocation = locations[0]
      setSelectedLocation(defaultLocation)
      fetchParams.redemptionLocationId = defaultLocation.id
      trackExperienceOfferEvent('REDEMPTION_LOCATION_SELECTED_NON_INTERACTIVE')
    }

    const pickups = experience.pickupPoints
    if (pickups?.length === 1) {
      const defaultPickup = pickups[0]
      setSelectedPickupPoint(defaultPickup)
      fetchParams.pickupPointId = defaultPickup.id
      trackExperienceOfferEvent('PICKUP_LOCATION_SELECTED_NON_INTERACTION')
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [experience, isBuyNowBookLater, isGift, selectedPickupPoint, date, setSelectedPickupPoint, trackExperienceOfferEvent, setSelectedLocation, setSelectedTicketMode])

  useEffect(() => {
    loadDates()
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceView])

  useEffect(() => {
    if (!availableDates?.length) return

    if (!experience.hasCalendar) {
      // Gift/BNBL modals share this context — setting date to undefined here
      // would wipe the date the main page depends on, causing tickets to vanish.
      // These modals don't need a context date (fetchTimeParams overrides it).
      if (isBuyNowBookLater || isGift) return

      // we still fetch calendar data even though it doesn't have it as we need it
      // to go fetch the times which gives us the tickets.
      // setDate clears ticketCounts + time in the reducer. Preserve any pre-loaded
      // values (e.g. from saved trip planner data) and restore them afterwards so
      // the auto-select guard in onSelectTimeSlot fires correctly and doesn't wipe
      // the restored counts on the next render cycle.
      const savedCounts = ticketCounts
      const savedTime = time
      setDate(availableDates[0].day)
      if (savedTime) {
        setTimeSlot(savedTime)
      }
      if (Object.keys(savedCounts).length > 0) {
        setTicketCounts(savedCounts)
      }
      trackExperienceOfferEvent('DATE_ON_CALENDAR_SELECTED_NON_INTERACTION')
      return
    }

    const urlDate = initialDateFromUrl.current
    initialDateFromUrl.current = undefined
    if (urlDate && urlDate !== date && availableDates.some(availableDate => availableDate.day === urlDate)) {
      setDate(urlDate)
    }
  }, [availableDates]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // After login the availableDates and availableTimes gets cleared
    // in the context, this will fire a refetch of dates and times
    // to update the limits for the new customer
    if (previousAvailableDates?.length && !availableDates?.length) {
      loadDates()
    }
  }, [previousAvailableDates, availableDates, loadDates])

  const onPickupPointSelect = useCallback((newPickupPoint?: App.ExperiencePickupPoint) => {
    if (newPickupPoint && selectedPickupPoint && newPickupPoint.id === selectedPickupPoint.id) return

    setSelectedPickupPoint(newPickupPoint)
    setDate()
    setTimeSlot('')
    setTicketCounts({})
  }, [setSelectedPickupPoint, selectedPickupPoint, setDate, setTimeSlot, setTicketCounts])

  const onLocationSelect = useCallback((newLocation?: App.ExperienceRedemptionLocation) => {
    if (newLocation && selectedLocation && newLocation.id === selectedLocation.id) return
    setSelectedLocation(newLocation)
    setDate()
    setTimeSlot('')
    setTicketCounts({})
  }, [setSelectedLocation, selectedLocation, setDate, setTimeSlot, setTicketCounts])

  const onTicketModeSelect = useCallback((newTicketModesMap?: Record<string, string>) => {
    if (ticketModeMapsEqual(newTicketModesMap, selectedTicketModesMap)) return

    setSelectedTicketMode(newTicketModesMap)
    setDate()
    setTimeSlot('')
    setTicketCounts({})
  }, [setSelectedTicketMode, selectedTicketModesMap, setDate, setTimeSlot, setTicketCounts])

  const onDateSelect = useCallback((newDate: App.ExperienceAvailabilityDate) => {
    if (date === newDate.day) return

    setDate(newDate.day)
    setTimeSlot('')
    setTicketCounts({})
  }, [setDate, date, setTimeSlot, setTicketCounts])

  const onSelectTimeSlot = useCallback((newTimeSlot: string) => {
    if (newTimeSlot === time) return
    // setTime clears ticketCounts in the reducer. When the time transitions from
    // unset to a slot (e.g. the non-interactive auto-select of the hidden single
    // timeslot on open-dated offers), preserve any pre-loaded counts (saved trip
    // planner data) and restore them — mirroring the no-calendar date restore above.
    // Counts only need wiping when changing between time slots.
    const savedCounts = !time && Object.keys(ticketCounts).length > 0 ? ticketCounts : undefined
    setTimeSlot(newTimeSlot)
    setTicketCounts(savedCounts ?? {})
  }, [setTimeSlot, time, ticketCounts, setTicketCounts])

  const handleTicketCountChange = useCallback((ticket: App.ExperienceBookingView['ticketOptions'][number], nextCount: number) => {
    if (hasNoTicketsSelected) setHasNoTicketsSelected(false)
    setTicketCounts({
      ...ticketCounts,
      [ticket.id]: nextCount,
    })
    onTicketCountChange((ticketCounts[ticket.id] ?? 0) < nextCount)
  }, [setTicketCounts, ticketCounts, onTicketCountChange, hasNoTicketsSelected])

  const handleLimitedAvailabilityBannerClick = useCallback(() => {
    scrollToId('offer-details')
  }, [])

  const allTicketOptions = useExperienceTicketsOptions(availableTimes, { isBuyNowBookLater, isGift, time })

  const counts = useMemo(() => {
    return Object.entries(ticketCounts).map(([ticketId, count]) => {
      return {
        ticket: allTicketOptions.find(t => t.id === ticketId)!,
        count,
      }
      // sometimes the tickets we previously selected are no longer available
      // remove them
    }).filter(count => !!count.ticket)
  }, [ticketCounts, allTicketOptions])

  const { totalNonExtraCountOnCart, totalCountOnCart } = useMemo(() => ({
    totalCountOnCart: sum(counts, count => count.count),
    totalNonExtraCountOnCart: sum(counts.filter(item => !item.ticket.isExtra), count => count.count),
  }), [counts])

  const [isFromPrice, totalPrice] = useMemo(() => {
    const ticketsById = arrayToObject(
      allTicketOptions,
      (ticket) => ticket.id,
    )
    const selectedTicketPrices = Object.entries(ticketCounts).flatMap(
      ([id, count]) => (ticketsById[id]?.price ?? 0) * count,
    )
    const selectedPrice = sum(selectedTicketPrices)
    return [
      selectedPrice === 0,
      selectedPrice || experienceView.price || 0,
    ]
  }, [ticketCounts, experienceView, allTicketOptions])

  const totalLuxPlusPrice = useMemo(() => {
    const ticketsById = arrayToObject(
      allTicketOptions,
      (ticket) => ticket.id,
    )
    const selectedTicketPrices = Object.entries(ticketCounts).flatMap(
      ([id, count]) => (ticketsById[id]?.memberPrice ?? 0) * count,
    )
    const selectedPrice = sum(selectedTicketPrices)
    return selectedPrice || experience.pricing.memberPrice
  }, [ticketCounts, allTicketOptions, experience.pricing.memberPrice])

  const orderMode = useMemo<App.ExperienceOrderMode>(() => {
    switch (true) {
      case isGift && experience.giftType === 'offer':
        return 'gift-offer'
      case isGift && experience.giftType === 'gift-card':
        return 'gift-card'
      case isBuyNowBookLater:
        return 'bnbl'
      default:
        return 'booking'
    }
  }, [isGift, isBuyNowBookLater, experience.giftType])

  const generateCartItem = useCallback((ticketData: ExperienceDetailsForm) => {
    if (isGift && experience.giftType === 'gift-card') {
      return generateExperienceGiftCardItem(
        experience,
        totalPrice,
        intl,
        counts,
      )
    } else {
      return generateExperienceCheckoutItem(
        experience,
        ticketData,
        counts,
        {
          isBuyNowBookLater,
          isGift,
          orderMode,
        },
      )
    }
  }, [counts, experience, intl, isBuyNowBookLater, isGift, totalPrice, orderMode])

  const appendToUserCart = useCallback((ticketData: ExperienceDetailsForm) => {
    const item = generateCartItem(ticketData)
    dispatch(addToUserCart([item]))
    trackEvent(addToCartMultiItem([item], { isMultiCart: true }))
  }, [dispatch, generateCartItem])

  const proceedToCheckout = useCallback((ticketData: ExperienceDetailsForm, keepBrowsing = false) => {
    const item = generateCartItem(ticketData)
    const initParams = isGift || checkoutSource ? {
      ...(isGift ? { isGift } : {}),
      ...(checkoutSource ? { checkoutSource } : {}),
    } : undefined
    dispatch(addToCheckoutCart([item], initParams))
    trackEvent(addToCart())
    if (keepBrowsing) {
      showSnackbar(intl.formatMessage({
        id: '8lxFOePJ8_3yc7gdvI8H',
        defaultMessage: 'Your item has been added to the cart.',
        description: 'Success message shown after adding an experience to the cart',
      }), 'success')
      return
    }
    dispatch(navigateToCheckout())
  }, [generateCartItem, dispatch, isGift, checkoutSource, intl])

  const submitForm = useCallback((submitAction: SubmitAction) => {
    if (!internalFormRef.current?.reportValidity()) return
    if (totalNonExtraCountOnCart === 0 || minSeats > totalUsedSeatsOnCart) {
      setHasNoTicketsSelected(true)
      return
    }
    handleOptionPurchaseEvent()
    const ticketData = formToObject<ExperienceDetailsForm>(internalFormRef.current)
    delete ticketData.ticketCount // Unneeded field
    counts.map(ticket => {
      addGTMEvent(addExperienceToCheckout(experienceView, ticket, currency))
    })

    const generatedTicketData = isBuyNowBookLater || isGift ?
        {
          date: undefined,
          language: selectedLanguage?.id,
          pickupPoint: selectedPickupPoint?.id,
          redemptionLocation: selectedLocation?.id,
          ticketModesMap: selectedTicketModesMap,
          time: ticketData.time,
        } : {
          ...ticketData,
          ticketModesMap: selectedTicketModesMap,
        }

    if (submitAction === 'addToUserCart') {
      appendToUserCart(generatedTicketData)
    } else {
      proceedToCheckout(generatedTicketData, submitAction === 'keepBrowsing')
    }

    const ticketQuantity = sum(counts ?? [], count => count.count)
    fireEcommerceGTMEvent(addToCartGTM(experience, currency, totalPrice, ticketQuantity, 'experience'))
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- onBuyNow is stable
  }, [counts, experience, currency, totalPrice, experienceView, isBuyNowBookLater, isGift, proceedToCheckout, selectedLanguage?.id, selectedLocation?.id, selectedPickupPoint?.id])

  const handleBuyNow = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    submitForm('buyNow')
  }, [submitForm])

  const handleAddToUserCart = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    submitForm('addToUserCart')
  }, [submitForm])
  const locationFieldLabel = intl.formatMessage({
    id: '96bXOVYIhWfQs3KxATGI',
    defaultMessage: 'location',
    description: 'Field label used in no-tickets messaging for the selected location on the experience page',
  })
  const dateFieldLabel = intl.formatMessage({
    id: 'ySIT44W-SB6W6dYa12jN',
    defaultMessage: 'date',
    description: 'Field label used in no-tickets messaging for the selected date on the experience page',
  })
  const timeFieldLabel = intl.formatMessage({
    id: 'EE2NHYPq_uSPCaHrMC9i',
    defaultMessage: 'time',
    description: 'Field label used in no-tickets messaging for the selected time on the experience page',
  })
  const languageFieldLabel = intl.formatMessage({
    id: 'hacB3cdh6kRRsjF5IoaA',
    defaultMessage: 'language',
    description: 'Field label used in no-tickets messaging for the selected language on the experience page',
  })

  const userSelectedFields = useMemo(() => {
    const fields: Array<string> = []
    if (experience.redemptionLocations.length > 1 || experience.pickupPoints.length > 1) {
      fields.push(locationFieldLabel)
    }
    if (experience.hasCalendar && !datesError) {
      fields.push(dateFieldLabel)

      if (experience.hasTimeslots && !timeSlotsError) {
        fields.push(timeFieldLabel)
      }
    }
    if (experience.languages.length > 1) {
      fields.push(languageFieldLabel)
    }
    return fields
  }, [dateFieldLabel, datesError, experience, languageFieldLabel, locationFieldLabel, timeFieldLabel, timeSlotsError])

  const currentTimeSlot = useMemo(() => {
    if (availableTimes) {
      if (isBuyNowBookLater || isGift) {
        let timeslots = availableTimes.filter(slot => slot.purchaseLimit.maxReason !== 'AVAILABILITY')
        if (!timeslots.length) timeslots = availableTimes

        return max<App.ExperienceAvailabilityTimeSlot>(timeslots, slot => slot.purchaseLimit.max)
      } else {
        return availableTimes.find(slot => slot.time === time)
      }
    }
  }, [availableTimes, time, isBuyNowBookLater, isGift])

  const { totalUsedSeatsOnCart, totalSeatsRemaining } = useMemo(() => {
    if (!currentTimeSlot) return { totalSeatsRemaining: 0, totalUsedSeatsOnCart: 0, totalUsedSeatsOnServer: 0 }
    return getTotalSeats(currentTimeSlot, ticketCounts)
  }, [ticketCounts, currentTimeSlot])

  const hasError = datesError || timeSlotsError
  const emptyTimeSlots = !!(date && !availableTimes.length)
  // for voucher based sometimes some tickets or all tickets will be sold out
  const offerWithAllTicketsSoldOut = emptyTimeSlots && !hasError && !loading

  const minSeats = currentTimeSlot?.purchaseLimit.min ?? 1

  const showLuxPlusPrice = useMemo(() => {
    return luxPlusEnabled && experience.pricing.memberPrice > 0
  }, [luxPlusEnabled, experience])

  const pointsEarnCalculationRequest = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (isFromPrice) {
      return [{
        // if lux plus is enabled, we need to use the member price
        price: showLuxPlusPrice ? totalLuxPlusPrice : totalPrice,
        loyaltyProductType: 'experience',
        offerId: experience.id,
      }]
    }

    const ticketsById = arrayToObject(allTicketOptions, ticket => ticket.id)

    const selectedTickets = Object.entries(ticketCounts).flatMap(([id, count]): Array<App.LoyaltyPointsEarnCalculationRequest> => {
      const ticket = ticketsById[id]
      if (!ticket) return []
      return Array.from({ length: count }, (): App.LoyaltyPointsEarnCalculationRequest => ({
        price: showLuxPlusPrice ? ticket.memberPrice || 0 : ticket.price || 0,
        loyaltyProductType: 'experience',
        offerId: experience.id,
      }))
    })

    return selectedTickets
  }, [allTicketOptions, ticketCounts, totalPrice, isFromPrice, showLuxPlusPrice, totalLuxPlusPrice, experience.id])

  const noTicketsWithFieldsMessage = intl.formatMessage(
    {
      id: 'OGlbZxBIzdSebKVi9kq7',
      defaultMessage: 'There are no tickets available for your selection, try another {fields}.',
      description: 'Error message when no tickets are available, suggesting the user try different selection fields',
    },
    { fields: arrayToStringListFormat(userSelectedFields, 'or', intl) },
  )
  const noTicketsMessage = intl.formatMessage({
    id: '1rwXmWU0tcdEG9L9edZs',
    defaultMessage: 'There are no tickets available for your selection.',
    description: 'Error message when no tickets are available for the selected options',
  })
  const noTicketsErrorMessage = userSelectedFields.length > 0 ? noTicketsWithFieldsMessage : noTicketsMessage
  const warningLabel = intl.formatMessage({
    id: 'RhOS9do_QRLHNKtPW3B3',
    defaultMessage: 'You should add at least one experience ticket',
    description: 'Warning message when user has not selected any experience tickets',
  })
  return (<VerticalSpacer as="form" gap={20} ref={internalFormRef}>
    {!isBuyNowBookLater && !isGift &&
      <TicketContainerWithoutPadding>
        <LoadingIndicator floating visible={loading} />
        <TicketInputContainer>
          <ExperienceTicketInputs
            experienceView={experienceView}
            onTimeSelect={onSelectTimeSlot}
            onPickupPointSelect={onPickupPointSelect}
            onLocationSelect={onLocationSelect}
            onTicketModeSelect={onTicketModeSelect}
            allDates={availableDates}
            selectedDate={date ?? ''}
            selectedTimeSlot={time ?? ''}
            defaultLanguageId={selectedLanguage?.id}
            defaultPickupPointId={selectedPickupPoint?.id}
            defaultRedemptionLocationId={selectedLocation?.id}
            defaultTicketModesMap={selectedTicketModesMap}
            availableTimes={availableTimes}
            onDateSelect={onDateSelect}
            onLanguageSelect={setLanguage}
            preserveSelectedDateWhenDateOptionsEmpty={preserveSelectedDateWhenDateOptionsEmpty}
            dateOptionsLoading={fetchingDates}
          />
          {(hasError || offerWithAllTicketsSoldOut) && <BodyText variant="large" colour="critical">
            {noTicketsErrorMessage}
          </BodyText>}
        </TicketInputContainer>
        {allTicketOptions.length > 0 && !!currentTimeSlot && <>
          <ExperienceTicketCustomerLimit
            purchaseLimit={currentTimeSlot.purchaseLimit}
            totalSeatsRemaining={totalSeatsRemaining}
            totalUsedSeatsOnCart={totalUsedSeatsOnCart}
          />
          {((totalNonExtraCountOnCart === 0 && totalCountOnCart > 0) || hasNoTicketsSelected) && (
            <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
              {warningLabel}
            </MessageBanner>
          )}
        </>}

      </TicketContainerWithoutPadding>}
    <LoadingIndicator floating visible={(isBuyNowBookLater || isGift) && loading} />
    {(isBuyNowBookLater || isGift) && ((totalNonExtraCountOnCart === 0 && totalCountOnCart > 0) || hasNoTicketsSelected) && (
      <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
        {warningLabel}
      </MessageBanner>
    )}
    {allTicketOptions.length > 0 && <TicketContainerWithoutPadding>
      <LoadingIndicator floating visible={(!isBuyNowBookLater || !isGift) && loading} />
      <TicketInputContainer>
        <ExperienceTicketSelection
          ticketOptions={allTicketOptions}
          ticketCounts={ticketCounts}
          onTicketCountChange={handleTicketCountChange}
          purchaseLimitRemaining={totalSeatsRemaining}
          purchaseLimitReason={currentTimeSlot?.purchaseLimit.maxReason}
          orderMode={orderMode}
          bookingType={experience.bookingType}
          onLimitedAvailabilityBannerClick={!isGift && !isBuyNowBookLater
            ? handleLimitedAvailabilityBannerClick
            : undefined}
          afterHeading={collinsonValidityNote}
          hideAvailabilityBanner={experience.provider === 'cln'}
        />
      </TicketInputContainer>
      {(isBuyNowBookLater || isGift) && !!currentTimeSlot && <ExperienceTicketCustomerLimit
        purchaseLimit={currentTimeSlot.purchaseLimit}
        totalSeatsRemaining={totalSeatsRemaining}
        totalUsedSeatsOnCart={totalUsedSeatsOnCart}
      />}
    </TicketContainerWithoutPadding>}
    <ExperienceOfferPageBuyBar
      experience={experience}
      totalPrice={totalPrice}
      totalLuxPlusPrice={totalLuxPlusPrice}
      isFromPrice={isFromPrice}
      showLuxPlusPrice={showLuxPlusPrice}
      pointsEarnCalculationRequest={pointsEarnCalculationRequest}
      userMultiItemCartSupported={userMultiItemCartSupported}
      onBuyNow={handleBuyNow}
      onAddToUserCart={handleAddToUserCart}
      secondaryAction={secondaryAction}
    />
  </VerticalSpacer>
  )
})

const mapStateToProps = (state: App.State) => {
  return {
    luxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(React.memo(ExperienceOfferPageTicketSelection))
